import { useMemo } from "react"
import { useAppSelector } from "../reducers/hooks"
import { selectChats } from "../reducers/chatsSliceSelectors"
import { MessagesState } from "../reducers/messagesSlice"
import { selectMessages } from "../reducers/messagesSliceSelectors"
import { Chat } from "../reducers/chatsSlice"
import { selectAccountChats } from "../reducers/accountChatsSlice"
import { selectArchivedChats } from "../reducers/chatsSettingsSelectors"
import type { RootState } from "../reducers/store"
import useSlickAISilencePredicate from "./useSlickAISilencePredicate"
import { findLastChatEvent } from "./useLastChatsEvent"

export const sortChats = (
  chats: Chat[],
  messages: MessagesState,
  isSlickAISilenced: (chatJid: string) => boolean = () => false,
) => {
  // The same last event the row shows as its preview — one implementation, so
  // the agent conversation stays out of both. The workspace flag is not passed
  // through here: for a reader with the column the preview also drops the
  // room's own slick:agent notes, while the order still follows them.
  const chatsWithLastEvent = chats?.map((chat) => ({
    chat,
    lastEvent: findLastChatEvent(
      messages?.[chat.jid],
      isSlickAISilenced(chat.jid),
    ),
  }))

  // Sort function based on the algorithm provided
  return chatsWithLastEvent
    .sort((a, b) => {
      // Draft messages take precedence
      if (a.chat.draftMessage !== b.chat.draftMessage) {
        return a.chat.draftMessage ? -1 : 1
      }

      const aTime = a.lastEvent ? Date.parse(a.lastEvent.timestamp) : 0
      const bTime = b.lastEvent ? Date.parse(b.lastEvent.timestamp) : 0

      return bTime - aTime
    })
    .map((item) => item.chat)
}

/** Whether `jid` is currently an available driver chat (non-archived room in
 * the "Drivers" group). On-demand — call via `store.getState()`. */
export function selectIsDriverChatJid(
  state: RootState,
  jid: string | null | undefined,
): boolean {
  if (!jid) return false
  const accountChats = selectAccountChats(state)
  if (accountChats[jid]?.groupName !== "Drivers") return false
  if (selectArchivedChats(state).includes(jid)) return false
  return Boolean(selectChats(state).find((c) => c.jid === jid)?.room)
}

/**
 * JID of the first driver chat (a non-archived room in the "Drivers" group).
 * Used as the fallback selection when there's no known previous active chat.
 * Plain on-demand selector — call it via `store.getState()` when needed.
 */
export function selectFirstDriverChatJid(state: RootState): string | undefined {
  const accountChats = selectAccountChats(state)
  const archived = selectArchivedChats(state)
  return selectChats(state).find(
    (chat) =>
      chat.room &&
      !archived.includes(chat.jid) &&
      accountChats[chat.jid]?.groupName === "Drivers",
  )?.jid
}

const useSortedChatsByLastEvent = () => {
  const chats = useAppSelector(selectChats)
  const messages = useAppSelector(selectMessages)
  const isSlickAISilenced = useSlickAISilencePredicate()

  return useMemo(
    () => sortChats(chats, messages, isSlickAISilenced),
    [chats, messages, isSlickAISilenced],
  )
}

export default useSortedChatsByLastEvent
